/**
 * HeroLaunch — hero «заход в ресторан» + «Запуск за 1 день» в ОДНОЙ
 * закреплённой секции с ОДНИМ видео (2026-06-20, по просьбе владельца —
 * полное слияние двух блоков, чтобы стык исчез полностью).
 *
 *   Раньше было два соседних блока, каждый со СВОИМ <video> (hero — первая
 *   половина клипа, «Запуск» — вторая). На границе картинка чуть
 *   «повторялась» (каждое видо — object-cover своего экрана). Теперь это
 *   ОДНО видео, чей currentTime непрерывно скрабится 0→duration за всю
 *   дорожку прокрутки → камера едет ко входу → в зал → к открытой кухне
 *   без единого шва.
 *
 *   Дорожка делится по прогрессу на две фазы (граница = SPLIT):
 *     · Фаза A (hero, p∈[0..SPLIT]): улица → дверь → зал. Поверх три слоя
 *       текста (intro CAFÉ OS → серединная строка → CTA + glass-панель).
 *     · Фаза B (launch, p∈[SPLIT..1]): доезд к кухне. Поверх — компактная
 *       шапка «Запуск за один день» + горизонтальный ряд карточек-шагов,
 *       всплывающих по одному слева-направо.
 *   На середине (p≈0.5) две группы оверлеев кросс-фейдятся, видео при этом
 *   едет непрерывно.
 *
 *   Мобильный / reduced-motion: scroll-scrub на телефонах дёргается, поэтому
 *   там — два обычных блока в потоке (hero автоплеем первой половины +
 *   «Запуск» автоплеем второй половины), как и было.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n";
import { DEMO_URL } from "../content/contacts";
import { useReveal } from "../lib/motion";
import { SectionHead } from "./SectionHead";
import { ENTRANCE_VIDEO, ENTRANCE_POSTER, HERO_CLIP_SPLIT } from "../lib/heroClip";

/**
 * Длина общей дорожки прокрутки в высотах экрана (десктоп). 560 → ~30vh на
 * секунду клипа: единая скорость скраба на обе фазы (как у прежних
 * раздельных блоков hero≈32 и process≈29). Удлинено 560→760 (просьба
 * владельца «удлинить видео»): проезд медленнее/кинематографичнее.
 * Закреплённая часть = 660vh.
 */
const TRACK_VH = 760;
/** Граница фаз A/B по прогрессу = доля клипа, проигранная в hero. */
const SPLIT = HERO_CLIP_SPLIT;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
/** Нормализованный «отрезок» прогресса [a..b] → [0..1]. */
const seg = (p: number, a: number, b: number): number => clamp01((p - a) / (b - a));
/** Появление в [inA..inB], исчезновение в [outA..outB]. */
const band = (p: number, inA: number, inB: number, outA: number, outB: number): number =>
  Math.min(seg(p, inA, inB), 1 - seg(p, outA, outB));

export function HeroLaunch() {
  const { t } = useLang();
  const n = t.process.steps.length;

  // — десктопный scroll-scrub —
  const trackRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const heroLayerRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const midRef = useRef<HTMLDivElement>(null);
  const outroRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const launchLayerRef = useRef<HTMLDivElement>(null);
  const launchScrimRef = useRef<HTMLDivElement>(null);
  const heroScrimRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  // true → десктопный scroll-scrub; false → мобильный автоплей-fallback
  const [scrub, setScrub] = useState<boolean | null>(null);

  // — мобильный fallback («Запуск»-блок) —
  // [scrub]: секция #process монтируется только при scrub===false (не на первом
  // кадре), поэтому reveal должен пере-настроиться, когда она появится, иначе её
  // контент навсегда остаётся opacity:0 (пустой блок под hero).
  const mLaunchRef = useReveal<HTMLElement>([scrub]);

  useEffect(() => {
    // Fallback (без scroll-scrub) на: узких экранах, reduced-motion И ЛЮБЫХ
    // сенсорных устройствах (pointer: coarse) — на телефоне/планшете любой
    // ширины/ориентации скраб не работает, и длинный видео-трек (760vh) между
    // фазами «01» и «02» читается как пустой провал. Мышиный десктоп → скраб.
    const mq = window.matchMedia(
      "(max-width: 820px), (prefers-reduced-motion: reduce), (pointer: coarse)",
    );
    const apply = () => setScrub(!mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // — Десктоп: одно видео 0→duration на всю дорожку + кросс-фейд оверлеев —
  useEffect(() => {
    if (scrub !== true) return;
    const track = trackRef.current;
    const video = videoRef.current;
    if (!track || !video) return;

    video.pause();

    let raf = 0;
    let targetP = 0; // прогресс 0..1 по всей дорожке
    let smTime = 0; // сглаженный currentTime
    let duration = 15;
    let off = false; // секция далеко за экраном → не сейкаем/не красим (перф)
    // Скраб не трогает currentTime, пока не докачано начало клипа: сейк в пустой
    // буфер стопорит декодер, и это читалось как «видео зависло». До готовности
    // виден чистый первый кадр (= постер), текстовые слои анимируются как обычно.
    const READY_LEAD = 1.5; // секунд с начала — этого хватает, чтобы поехать
    let ready = false;
    const headBuffered = (): boolean => {
      const b = video.buffered;
      return b.length > 0 && b.start(0) <= 0.05 && b.end(0) >= Math.min(READY_LEAD, duration - 0.2);
    };

    const onMeta = () => {
      duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 15;
    };
    video.addEventListener("loadedmetadata", onMeta);
    if (video.readyState >= 1) onMeta();

    const measure = () => {
      const rect = track.getBoundingClientRect();
      off = rect.bottom < -400 || rect.top > window.innerHeight + 400;
      const total = track.offsetHeight - window.innerHeight;
      const passed = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      targetP = total > 0 ? passed / total : 0;
    };

    const startAt = 0.06;
    const stepGap = 0.62 / Math.max(n, 1);

    const paint = (p: number) => {
      const pa = clamp01(p / SPLIT); // прогресс фазы A (hero)
      const pb = clamp01((p - SPLIT) / (1 - SPLIT)); // прогресс фазы B (launch)
      // «Бит» на КОНТЕНТЕ (видео при этом НЕ стоит): hero-контент формируется к
      // p≈0.45 и держится до 0.55 (видео скрабится за ним) → ощущение паузы без
      // зависания, затем смена на «Запуск» в [0.55..0.65].
      const heroAlpha = 1 - seg(p, 0.55, 0.65);
      const launchAlpha = seg(p, 0.55, 0.65);

      if (heroLayerRef.current) heroLayerRef.current.style.opacity = String(heroAlpha);
      if (launchLayerRef.current) launchLayerRef.current.style.opacity = String(launchAlpha);
      if (launchScrimRef.current) launchScrimRef.current.style.opacity = String(launchAlpha);
      // Постоянную вуаль гасим к фазе «Запуск», чтобы не складывалась с
      // launchScrim (двойное затемнение → «мутный» второй блок).
      if (heroScrimRef.current) heroScrimRef.current.style.opacity = String(heroAlpha);

      // hero: три слоя текста проявляются по фазам pa
      if (introRef.current) {
        const o = 1 - seg(pa, 0.26, 0.44);
        introRef.current.style.opacity = String(o);
        introRef.current.style.transform = `translateY(${(-26 * seg(pa, 0.1, 0.44)).toFixed(1)}px) scale(${(1 - 0.06 * seg(pa, 0.1, 0.44)).toFixed(3)})`;
      }
      if (midRef.current) {
        const o = band(pa, 0.34, 0.48, 0.6, 0.72);
        midRef.current.style.opacity = String(o);
        midRef.current.style.transform = `translateY(${(18 - 30 * seg(pa, 0.34, 0.72)).toFixed(1)}px)`;
      }
      if (outroRef.current) {
        const o = seg(pa, 0.72, 0.9);
        outroRef.current.style.opacity = String(o);
        outroRef.current.style.transform = `translateY(${(28 - 28 * seg(pa, 0.72, 0.94)).toFixed(1)}px)`;
        // кликабельно только когда видно И мы ещё в hero-фазе (не перекрывает launch)
        outroRef.current.style.pointerEvents = o > 0.6 && heroAlpha > 0.5 ? "auto" : "none";
      }
      if (hintRef.current) {
        hintRef.current.style.opacity = String((1 - seg(pa, 0.05, 0.12)) * heroAlpha);
      }

      // launch: карточки всплывают по очереди слева-направо по pb
      cardsRef.current.forEach((el, i) => {
        if (!el) return;
        const o = clamp01((pb - (startAt + i * stepGap)) / (stepGap * 0.85));
        el.style.opacity = String(o);
        el.style.transform = `scale(${(0.96 + 0.04 * o).toFixed(3)})`;
      });
    };

    const tick = () => {
      // try/catch вокруг всего тела: что бы ни случилось (seek отклонён, paint
      // кинул) — rAF-петля ВСЕГДА перепланируется и не «умирает» (иначе видео
      // зависает навсегда). За экраном — холостой кадр (освобождаем декодер).
      try {
        if (!off) {
          if (!ready && headBuffered()) ready = true;
          const want = targetP * duration; // непрерывный проезд 0→duration (без паузы — она читалась как «зависание»)
          smTime += (want - smTime) * 0.18;
          if (Math.abs(want - smTime) < 0.01) smTime = want;
          // ГЕЙТ НА `!video.seeking` — корневой фикс «замирает/дёргается».
          // Раньше currentTime назначался на КАЖДОМ rAF-кадре (60×/с) без
          // ожидания завершения предыдущего сейка → сейки наслаивались, декодер
          // не успевал, и currentTime фактически НЕ двигался (замер на месте при
          // полной прокрутке дорожки доезжал лишь до ~0.1с из 15с) + рывки.
          // Теперь сейкаем ТОЛЬКО когда декодер свободен и сдвиг ≥ ~одного кадра.
          if (ready && video.readyState >= 2 && !video.seeking && Number.isFinite(smTime)) {
            // Отступ 0.15с от конца: видео НИКОГДА не достигает `ended` —
            // иначе рывок currentTime↔duration и зависший seek в конце дорожки.
            let target = Math.min(Math.max(smTime, 0), duration - 0.15);
            // Сейк ТОЛЬКО в забуференное: seek в недокачанную часть стопорит
            // декодер до конца скачивания файла → «видео зависает» при быстрой
            // прокрутке на небыстрой сети. Если цель дальше буфера —
            // прижимаемся к его краю, кадр догоняет по мере докачки.
            const b = video.buffered;
            let ok = false;
            let edge = -1;
            for (let i = 0; i < b.length; i++) {
              if (target >= b.start(i) && target <= b.end(i)) {
                ok = true;
                break;
              }
              // Запас 0.25с от края: край буфера «дышит», а GOP=8 требует, чтобы
              // рядом лежал опорный кадр — иначе сейк снова упрётся в декодер.
              if (b.end(i) <= target && b.end(i) > edge) edge = b.end(i) - 0.25;
            }
            if (!ok) {
              if (edge < 0) target = video.currentTime; // ничего не докачано впереди — холостой кадр
              else target = edge;
            }
            if (Math.abs(target - video.currentTime) >= 0.033) {
              try {
                video.currentTime = target;
              } catch {
                /* seek может отклониться, если буфер ещё не готов — пропускаем кадр */
              }
            }
          }
          paint(targetP);
        }
      } catch {
        /* никогда не роняем rAF-петлю */
      }
      raf = requestAnimationFrame(tick);
    };

    measure();
    paint(0);
    raf = requestAnimationFrame(tick);
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      video.removeEventListener("loadedmetadata", onMeta);
    };
  }, [scrub, n]);

  // — Мобильный / reduced-motion: два блока в потоке —
  if (scrub === false) {
    return (
      <>
        {/* HERO (автоплей первой половины) */}
        <section id="top" className="relative flex min-h-[100dvh] flex-col overflow-hidden">
          {/* Мобильный hero-фон = статичный постер (110 КБ), НЕ 11.6 МБ autoplay-
              видео: на телефоне (4G/слабый декодер) <video autoPlay> рендерил
              чёрный кадр поверх постера → «пропасть» под шапкой. Из блока «Запуск»
              фон-видео уже убрали по той же причине; здесь — тоже. Scroll-scrub на
              мобиле и так отключён, видео было чисто декоративным. */}
          <div className="hero-scene absolute inset-0" aria-hidden>
            <img
              className="hero-video absolute inset-0 h-full w-full object-cover"
              src={ENTRANCE_POSTER}
              alt=""
              loading="eager"
              decoding="async"
            />
          </div>
          <div className="hero-vignette absolute inset-0" aria-hidden />
          {/* Затемнение для читаемости мелкого текста (как на десктопе). */}
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden
            style={{
              background:
                "linear-gradient(180deg, rgba(8,7,6,0.70) 0%, rgba(8,7,6,0.34) 30%, rgba(8,7,6,0.34) 58%, rgba(8,7,6,0.84) 100%)",
            }}
          />
          <HeroCopy t={t} />
        </section>

        {/* ЗАПУСК — чистый тёмный блок (на мобиле без капризного фон-видео:
            оно дёргалось и показывало лишь половину кадра — фидбек владельца
            2026-06-28). Видео остаётся только в hero-блоке выше. */}
        <section
          id="process"
          ref={mLaunchRef}
          className="relative border-t"
          style={{ borderColor: "var(--line)", background: "var(--bg-deep)" }}
        >
          <div
            className="pointer-events-none absolute inset-0 z-0"
            aria-hidden
            style={{
              background:
                "radial-gradient(130% 70% at 50% 0%, rgba(58,42,22,0.45), rgba(10,9,8,0) 58%)",
            }}
          />

          <div
            className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8"
            style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}
          >
            <div style={{ textShadow: "0 2px 14px rgba(0,0,0,0.9)" }}>
              <SectionHead
                num="02"
                label={t.process.label}
                title={t.process.title}
                accent={t.process.accent}
                aside={t.process.aside}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {t.process.steps.map((step, i) => (
                <div
                  key={i}
                  className="reveal rounded-2xl border p-6 backdrop-blur-md"
                  data-reveal=""
                  style={
                    {
                      background: "rgba(12,10,8,0.6)",
                      borderColor: "var(--line-strong)",
                      "--reveal-delay": `${i * 80}ms`,
                    } as React.CSSProperties
                  }
                >
                  <div className="mb-5 flex items-start justify-between gap-3">
                    <span className="t-brand text-4xl" style={{ color: "rgba(244,239,230,0.24)" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className="rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em]"
                      style={{ borderColor: "var(--gold-line)", color: "var(--gold-bright)" }}
                    >
                      {step.when}
                    </span>
                  </div>
                  <h3 className="t-display mb-2.5 text-lg" style={{ color: "var(--fg)" }}>
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--fg-soft)" }}>
                    {step.body}
                  </p>
                </div>
              ))}
            </div>

            <p className="reveal mt-10 text-center text-base" data-reveal="" style={{ color: "var(--fg-muted)" }}>
              {t.process.footnote}
            </p>
          </div>
        </section>
      </>
    );
  }

  // — Десктоп (и SSR/первый кадр при scrub === null): единая секция, одно видео —
  return (
    <section id="top" ref={trackRef} className="relative" style={{ height: `${TRACK_VH}vh`, background: "var(--bg-deep)" }}>
      {/* Якорь для Rail-навигации → фаза «Запуск» (≈ середина дорожки) */}
      <div id="process" className="absolute inset-x-0" style={{ top: "41%", height: 1 }} aria-hidden />

      <div className="sticky top-0 flex h-[100dvh] flex-col overflow-hidden">
        {/* ОДНО видео на весь проезд (0→duration), непрерывный scroll-scrub.
            Монтируем <video> ТОЛЬКО при scrub===true: на первом кадре
            (scrub===null) эта ветка рендерится и на телефоне, и preload="auto"
            успевал запустить скачивание 2.7 МБ ещё до переключения на
            мобильный фолбэк с постером. До вердикта — тот же постер картинкой. */}
        <div className="hero-scene absolute inset-0" aria-hidden>
          {scrub === true ? (
            <video
              ref={videoRef}
              className="hero-video absolute inset-0 h-full w-full object-cover"
              src={ENTRANCE_VIDEO}
              poster={ENTRANCE_POSTER}
              muted
              playsInline
              preload="auto"
            />
          ) : (
            <img
              className="hero-video absolute inset-0 h-full w-full object-cover"
              src={ENTRANCE_POSTER}
              alt=""
              loading="eager"
              decoding="async"
            />
          )}
        </div>
        <div className="hero-vignette absolute inset-0" aria-hidden />
        {/* Затемнение для читаемости мелкого текста (просьба владельца «мелкие
            слова не видны / изменить свет»): темнее сверху (кикер/навбар) и снизу
            (CTA/glass), светлее в центре под крупным CAFÉ OS. Постоянно на обеих фазах. */}
        <div
          ref={heroScrimRef}
          className="pointer-events-none absolute inset-0 z-[1]"
          aria-hidden
          style={{
            background:
              "linear-gradient(180deg, rgba(8,7,6,0.68) 0%, rgba(8,7,6,0.32) 26%, rgba(8,7,6,0.30) 54%, rgba(8,7,6,0.82) 100%)",
          }}
        />
        {/* Вуаль фазы «Запуск» — проявляется по launchAlpha, чтобы текст/карточки читались над светлой частью кадра */}
        <div
          ref={launchScrimRef}
          className="pointer-events-none absolute inset-0 z-[1]"
          aria-hidden
          style={{
            opacity: 0,
            background:
              "linear-gradient(180deg, rgba(10,9,8,0.42) 0px, rgba(10,9,8,0.5) 220px, rgba(10,9,8,0.46) 60%, rgba(10,9,8,0.8) 100%)",
          }}
        />

        {/* ===== ГРУППА A — hero (кросс-фейд по heroAlpha) ===== */}
        <div ref={heroLayerRef} className="pointer-events-none absolute inset-0 z-10">
          {/* Слой 1 — intro: кикер + гигантский CAFÉ OS */}
          <div
            ref={introRef}
            className="absolute inset-0 mx-auto flex max-w-5xl flex-col items-center justify-center px-5 text-center sm:px-8"
          >
            <p className="t-eyebrow mb-8" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.92)" }}>
              <span className="gold-dot mr-3 inline-block align-middle" />
              {t.hero.eyebrow}
            </p>
            <h1 className="hero-title" style={{ fontSize: "var(--text-hero)" }}>
              ORDA<span style={{ color: "var(--gold-bright)" }}>&nbsp;OS</span>
            </h1>
          </div>

          {/* Слой 2 — серединная курсивная строка (момент входа в зал) */}
          <div
            ref={midRef}
            className="absolute inset-0 mx-auto flex max-w-4xl flex-col items-center justify-center px-6 text-center"
            style={{ opacity: 0 }}
          >
            <p
              className="t-serif-i"
              style={{
                fontSize: "clamp(1.6rem, 1rem + 2.8vw, 3.4rem)",
                color: "var(--fg)",
                lineHeight: 1.18,
                textShadow: "0 8px 50px rgba(0,0,0,.7)",
              }}
            >
              {t.hero.serifLine}
            </p>
          </div>

          {/* Слой 3 — outro: CTA + glass-панель (мы уже внутри зала) */}
          <div
            ref={outroRef}
            className="absolute inset-x-0 bottom-0 mx-auto flex w-full max-w-4xl flex-col items-center px-5 pb-[8vh] text-center sm:px-8"
            style={{ opacity: 0 }}
          >
            <p className="t-serif-i mb-7" style={{ fontSize: "clamp(1.15rem,.9rem+1.4vw,1.9rem)", color: "var(--fg-soft)" }}>
              {t.hero.serifLine}
            </p>
            <div className="mb-9 flex flex-wrap items-center justify-center gap-4">
              <a href={DEMO_URL} target="_blank" rel="noreferrer" className="btn-gold">
                {t.hero.ctaDemo}
                <ArrowUpRight className="h-4 w-4" aria-hidden />
              </a>
              <a href="#lead" className="btn-ghost">
                {t.hero.ctaContact}
              </a>
            </div>
            <dl className="hero-glass grid w-full grid-cols-1 divide-y divide-white/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <GlassStat label={t.hero.glass.platform.label} value={t.hero.glass.platform.value} />
              <GlassStat label={t.hero.glass.launch.label} value={t.hero.glass.launch.value} />
              <GlassStat label={t.hero.glass.langs.label} value={t.hero.glass.langs.value} />
            </dl>
          </div>

          {/* Подсказка «прокрутите» */}
          <div ref={hintRef} className="absolute inset-x-0 bottom-7 flex flex-col items-center gap-2" aria-hidden>
            <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: "var(--fg-muted)" }}>
              {t.hero.scrollHint}
            </span>
            <span className="hero-scroll-cue" />
          </div>
        </div>

        {/* ===== ГРУППА B — launch (кросс-фейд по launchAlpha) ===== */}
        <div ref={launchLayerRef} className="pointer-events-none absolute inset-0 z-10" style={{ opacity: 0 }}>
          <div className="relative mx-auto flex h-full w-full max-w-7xl flex-col justify-center px-5 py-16 sm:px-8">
            {/* Компактная шапка + описание (тень — чтобы читалось над светлой частью кадра) */}
            <div
              className="mb-9 max-w-3xl"
              style={{ textShadow: "0 2px 14px rgba(0,0,0,0.95), 0 0 44px rgba(0,0,0,0.7)" }}
            >
              <div className="section-rule mb-5">
                <span className="t-eyebrow">
                  <b>02</b>&nbsp;/&nbsp;{t.process.label}
                </span>
              </div>
              <h2 className="t-display" style={{ fontSize: "clamp(1.9rem, 1rem + 2.6vw, 3.2rem)", lineHeight: 1.04 }}>
                {t.process.title}{" "}
                <em className="t-serif-i" style={{ color: "var(--fg-soft)", fontWeight: 500 }}>
                  {t.process.accent}
                </em>
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>
                {t.process.aside}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-5">
              {t.process.steps.map((step, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    cardsRef.current[i] = el;
                  }}
                  className="rounded-2xl border p-6 will-change-transform"
                  style={{
                    opacity: 0,
                    background: "rgba(14,12,10,0.82)",
                    borderColor: "var(--line-strong)",
                  }}
                >
                  <div className="mb-5 flex items-start justify-between gap-3">
                    <span className="t-brand text-4xl sm:text-5xl" style={{ color: "rgba(244,239,230,0.22)" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className="rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em]"
                      style={{ borderColor: "var(--gold-line)", color: "var(--gold-bright)" }}
                    >
                      {step.when}
                    </span>
                  </div>
                  <h3 className="t-display mb-2.5 text-lg sm:text-xl" style={{ color: "var(--fg)" }}>
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--fg-soft)" }}>
                    {step.body}
                  </p>
                </div>
              ))}
            </div>

            <p className="mt-8 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
              {t.process.footnote}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Центральный текст для мобильного hero-fallback. */
function HeroCopy({ t }: { t: ReturnType<typeof useLang>["t"] }) {
  return (
    <>
      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-1 flex-col items-center justify-center px-5 pb-12 pt-32 text-center sm:px-8">
        <p className="t-eyebrow mb-8">
          <span className="gold-dot mr-3 inline-block align-middle" />
          {t.hero.eyebrow}
        </p>
        <h1 className="hero-title mb-4" style={{ fontSize: "var(--text-hero)" }}>
          ORDA<span style={{ color: "var(--gold-bright)" }}>&nbsp;OS</span>
        </h1>
        <p
          className="mb-6 max-w-2xl"
          style={{ fontSize: "clamp(0.82rem, 0.72rem + 0.5vw, 1rem)", color: "var(--gold)", lineHeight: 1.5, letterSpacing: "0.01em" }}
        >
          {t.hero.nameMeaning}
        </p>
        <p
          className="t-serif-i mb-10 max-w-3xl"
          style={{ fontSize: "clamp(1.3rem, 0.9rem + 2vw, 2.4rem)", color: "var(--fg-soft)", lineHeight: 1.2 }}
        >
          {t.hero.serifLine}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a href={DEMO_URL} target="_blank" rel="noreferrer" className="btn-gold">
            {t.hero.ctaDemo}
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </a>
          <a href="#lead" className="btn-ghost">
            {t.hero.ctaContact}
          </a>
        </div>
      </div>
      <div className="relative z-10 mx-auto w-full max-w-4xl px-5 pb-9 sm:px-8">
        <dl className="hero-glass grid grid-cols-1 divide-y divide-white/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <GlassStat label={t.hero.glass.platform.label} value={t.hero.glass.platform.value} />
          <GlassStat label={t.hero.glass.launch.label} value={t.hero.glass.launch.value} />
          <GlassStat label={t.hero.glass.langs.label} value={t.hero.glass.langs.value} />
        </dl>
      </div>
    </>
  );
}

function GlassStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 px-6 py-5 text-center">
      <dt
        className="text-[10px] font-bold uppercase tracking-[0.3em]"
        style={{ color: "var(--fg-soft)", textShadow: "0 1px 10px rgba(0,0,0,0.8)" }}
      >
        {label}
      </dt>
      <dd className="text-sm font-bold sm:text-[15px]" style={{ color: "var(--fg)", textShadow: "0 1px 10px rgba(0,0,0,0.85)" }}>
        {value}
      </dd>
    </div>
  );
}
